import { createSlice } from '@reduxjs/toolkit';
import { arrayify } from '../../../utils/array/array';
import { ReadOnlyArray } from '../../../utils/array/ReadOnlyArray';
import {
  STATE_UNPUBLISHED,
  STATE_UNTRANSLATED,
} from '../../../utils/category/states';
import { ReadOnlyObject } from '../../../utils/object/ReadOnlyObject';
import { getCategoryStatus } from '../../../utils/tree/getCategoryStatus';
import { getCountedRelations } from '../../../utils/tree/path';
import { setMarket } from '../intl';
import { mergeStateReducerBuilder } from '../../reducers/generic/mergeState';
import {
  createReferenceEqualSelector,
  getIdFromArgument,
} from '../../selectors/utils';
import type { RootState } from '../..';
import type { NodeBase } from '../../../entities/Node';

interface IndicatorState {
  categoryStatus: {
    [key: string]: unknown;
  };
  unpublishedCategories: {
    catalogId: string;
    categoryId: string;
    paths: string[];
  }[];
  untranslatedCategories: {
    catalogId: string;
    categoryId: string;
    paths: string[];
  }[];
  invalidCategoryImages: {
    catalogId: string;
    categoryId: string;
    paths: string[];
  }[];
}

const initialState: IndicatorState = {
  categoryStatus: {},
  unpublishedCategories: [],
  untranslatedCategories: [],
  invalidCategoryImages: [],
};
const indicatorSlice = createSlice({
  name: 'indicator',
  initialState,
  reducers: {
    updateCategoryStatusFromTreeData: {
      prepare(treeData: NodeBase[], catalogId: string) {
        return {
          meta: null,
          error: null,
          payload: {
            catalogId,
            ...getCategoryStatus(treeData),
          },
        };
      },
      reducer(state: IndicatorState, action) {
        const {
          catalogId,
          unpublishedCategories,
          untranslatedCategories,
          invalidCategoryImages,
        } = action.payload;

        state.categoryStatus[catalogId] = {
          [STATE_UNPUBLISHED]: getCountedRelations(unpublishedCategories),
          [STATE_UNTRANSLATED]: getCountedRelations(untranslatedCategories),
        };

        state.unpublishedCategories = state.unpublishedCategories
          .filter((c) => c.catalogId !== catalogId)
          .concat(unpublishedCategories);
        state.untranslatedCategories = state.untranslatedCategories
          .filter((c) => c.catalogId !== catalogId)
          .concat(untranslatedCategories);

        state.invalidCategoryImages = state.invalidCategoryImages
          .filter((c) => c.catalogId !== catalogId)
          .concat(invalidCategoryImages);
      },
    },
  },
  extraReducers: (builder) =>
    mergeStateReducerBuilder('state.indicator', builder).addCase(setMarket, () => ({
      ...initialState,
    })),
});

export const { updateCategoryStatusFromTreeData } = indicatorSlice.actions;
export default indicatorSlice.reducer;

const createIndicatorSelector = (state: RootState) => state.indicator;

export const selectCategoriesStatusByCatalogId = createReferenceEqualSelector(
  createIndicatorSelector,
  getIdFromArgument,
  (state, catalogId) => state.categoryStatus[catalogId] || ReadOnlyObject.Empty,
);

export const selectUnpublishedCategories = createReferenceEqualSelector(
  createIndicatorSelector,
  (state) => {
    const categories = state.unpublishedCategories;
    return categories.length ? categories : ReadOnlyArray.Empty;
  },
);

export const selectUnpublishedCategoriesByCategoryId = createReferenceEqualSelector(
  createIndicatorSelector,
  getIdFromArgument,
  (state, categoryId) =>
    arrayify(state.unpublishedCategories).filter((c) => c.categoryId === categoryId),
);

export const selectUntranslatedCategories = createReferenceEqualSelector(
  createIndicatorSelector,
  (state) => {
    const categories = state.untranslatedCategories;
    return categories.length ? categories : ReadOnlyArray.Empty;
  },
);

export const selectInvalidImages = createReferenceEqualSelector(
  createIndicatorSelector,
  (state) => {
    const categories = state.invalidCategoryImages;
    return categories.length ? categories : [];
  },
);

export const selectUntranslatedCategoriesByCategoryId = createReferenceEqualSelector(
  createIndicatorSelector,
  getIdFromArgument,
  (state, categoryId) =>
    arrayify(state.untranslatedCategories).filter(
      (c) => c.categoryId === categoryId,
    ),
);
